import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { status } from 'constants/general';
import { useDebouncedValue } from 'modules/common/hooks/useDebouncedValue';
import { PRODUCT_SEARCH_DEBOUNCE_MS, PRODUCT_SEARCH_PAGE_SIZE } from '../../constants';
import { searchProducts } from '../../actions';
import type { DiscountProduct } from '../../types/discount.types';
import { hasMorePages } from '../../utils/discountHelpers';
import { useInfiniteScroll } from 'modules/common/hooks/useInfiniteScroll';

interface UseProductsSearchOptions {
  isOpen: boolean;
  enabled?: boolean;
  debounceMs?: number;
  productType?: number;
  offerEligibleOnly?: boolean;
}

export function useProductsSearch({
  isOpen,
  enabled = true,
  debounceMs = PRODUCT_SEARCH_DEBOUNCE_MS,
  productType,
  offerEligibleOnly,
}: UseProductsSearchOptions) {
  const dispatch = useDispatch();
  const productSearchResults = useSelector(
    (state: any) => state.pharmacyDiscounts.productSearchResults,
  );
  const productSearchResultsLoadStatus = useSelector(
    (state: any) => state.pharmacyDiscounts.productSearchResultsLoadStatus,
  );

  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, debounceMs);
  const [allSearchResults, setAllSearchResults] = useState<DiscountProduct[]>([]);
  const [searchPageNumber, setSearchPageNumber] = useState(1);
  const [activeSearchQuery, setActiveSearchQuery] = useState('');
  const isSearchLoadMoreRef = useRef(false);

  const isLoadingSearch =
    productSearchResultsLoadStatus === status.FETCHING && !isSearchLoadMoreRef.current;
  const isLoadingMore =
    productSearchResultsLoadStatus === status.FETCHING && isSearchLoadMoreRef.current;
  const hasMore = productSearchResults
    ? hasMorePages(productSearchResults.items.length, PRODUCT_SEARCH_PAGE_SIZE)
    : false;

  const runSearch = useCallback(
    (query: string, page: number) => {
      const trimmedQuery = query.trim();
      if (!trimmedQuery) return;

      setActiveSearchQuery(trimmedQuery);
      const isLoadMore = page > 1;
      isSearchLoadMoreRef.current = isLoadMore;

      if (!isLoadMore) {
        setSearchPageNumber(1);
        setAllSearchResults([]);
      } else {
        setSearchPageNumber(page);
      }

      dispatch(
        searchProducts({
          searchTerm: trimmedQuery,
          pageNumber: page,
          pageSize: PRODUCT_SEARCH_PAGE_SIZE,
          ...(productType !== undefined ? { productType } : {}),
          ...(offerEligibleOnly !== undefined ? { offerEligibleOnly } : {}),
        }),
      );
    },
    [dispatch, productType, offerEligibleOnly],
  );

  const loadMore = useCallback(() => {
    runSearch(activeSearchQuery, searchPageNumber + 1);
  }, [runSearch, activeSearchQuery, searchPageNumber]);

  useEffect(() => {
    if (isOpen) return;
    setSearchQuery('');
    setAllSearchResults([]);
    setSearchPageNumber(1);
    setActiveSearchQuery('');
    isSearchLoadMoreRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSearchQuery('');
    setAllSearchResults([]);
    setSearchPageNumber(1);
    setActiveSearchQuery('');
    isSearchLoadMoreRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !enabled || debouncedSearchQuery.trim().length < 1) return;
    runSearch(debouncedSearchQuery, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, isOpen, enabled]);

  useEffect(() => {
    if (productSearchResultsLoadStatus !== status.SUCCESS || !productSearchResults) return;
    if (isSearchLoadMoreRef.current) {
      setAllSearchResults(prev => [...prev, ...productSearchResults.items]);
      isSearchLoadMoreRef.current = false;
    } else {
      setAllSearchResults(productSearchResults.items);
    }
  }, [productSearchResults, productSearchResultsLoadStatus]);

  const { containerRef: scrollContainerRef, sentinelRef: loadMoreSentinelRef } = useInfiniteScroll({
    hasMore,
    enabled: isOpen && !!activeSearchQuery && !isLoadingSearch,
    isLoadingMore,
    onLoadMore: loadMore,
  });

  const resetSearch = () => {
    setSearchQuery('');
    setAllSearchResults([]);
    setSearchPageNumber(1);
    setActiveSearchQuery('');
    isSearchLoadMoreRef.current = false;
  };

  return {
    searchQuery,
    setSearchQuery,
    allSearchResults,
    hasMore,
    isLoadingSearch,
    isLoadingMore,
    scrollContainerRef,
    loadMoreSentinelRef,
    resetSearch,
  };
}
